import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { message } from '~/components/Message'
import { useModalOpened, useModalToggle } from '~/state/application/hooks'
import { useTokenlonConfig, useInputAmountVerify } from '~/state/dex/hooks'
import { ApplicationModal } from '~/state/application/reducer'
import { Box } from '~/components/Box'
import {
  StyledSlippageBox,
  StyledLabelWrapper,
  StyledIcon,
  StyledLabel,
  StyledSlippageSelector,
  StyledSlippageSelectorItem,
  StyledSlippageInput,
  StyledTips,
} from './styled'
import { useGA, useUpdaterChainId } from '~/hooks'
import AuthorizationManage from './components/AuthorizationManage'
import PayGasFeeSetting from './components/PayGasFeeSetting'
import { RowBetween } from '~/components/Layout'
import IconSlippage from '~/assets/images/settings-slippage.svg'
import { gt, lt } from '../../helpers'
import { SlippageItem, useDefaultSlippage } from '../../hooks/useSlippage'
import { getLocalStorage } from '~/utils/common'

interface SettingsModalProps {
  slippageValue: number
  setSlippageItem: (chainId: number, slippageItem: SlippageItem) => void
}

const slippageSelectorValue = ['1.0', '2.0', '3.0']

const SettingsModal: React.FC<SettingsModalProps> = ({
  slippageValue,
  setSlippageItem,
}) => {
  const { t } = useTranslation()
  const { trackClick } = useGA()
  const chainId = useUpdaterChainId()
  const { slippageConfig } = useTokenlonConfig()
  const max = slippageConfig?.max || 5
  const min = slippageConfig?.min || 0.1
  const defaultSlippage = useDefaultSlippage()
  const slippages = getLocalStorage('slippages')
  const userModified = !!slippages?.[chainId]?.userModified
  const isDefaultSlippage = !userModified && slippageValue === defaultSlippage
  const isCustomSlippage = !slippageSelectorValue.includes(
    slippageValue.toFixed(1),
  )
  const initInputValue = isCustomSlippage ? slippageValue.toFixed(1) : ''

  const [inputValue, setInputValue] = useState<string>(initInputValue)
  const [isInputValid, setIsInputValid] = useState<boolean>(true)
  const [isInputFocus, setIsInputFocus] = useState<boolean>(false)

  const toggleModal = useModalToggle(ApplicationModal.SPOT_SWAP_SETTINGS)
  const isOpenModal = useModalOpened(ApplicationModal.SPOT_SWAP_SETTINGS)

  const verifyInputAmount = useInputAmountVerify()

  const onClosed = () => {
    toggleModal()
  }

  const handleClickSlippageSelector = (value: string) => {
    trackClick('webtl_instant_setting_slippage', {
      value: Number(value) / 100,
      type: 'button',
    })
    setSlippageItem(chainId, { value: Number(value), userModified: true })
    message.success(t('setting_slippage_toast'))
  }

  const handleInputFocus = () => {
    setIsInputFocus(true)
  }

  const handleInputBlur = () => {
    setIsInputFocus(false)
  }

  const handleInputSlippage = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value

    if (value.startsWith('00.')) value = value.substring(1)
    if (value.startsWith('.') && value.length > 1) value = String(Number(value))

    try {
      const [_, digit] = value.split('.')
      if (digit) {
        if (digit.includes('.')) return
        if (digit.length > 1) return
      }
    } catch (error) {
      console.error(error)
      setIsInputValid(false)
    }

    if (verifyInputAmount(value) || value === '') {
      setInputValue(value)
      if (gt(value, max) || lt(value, min)) setIsInputValid(false)
      else {
        trackClick('webtl_instant_setting_slippage', {
          value: Number(value) / 100,
          type: 'custom',
        })
        setIsInputValid(true)
        setSlippageItem(chainId, { value: Number(value), userModified: true })
      }
    } else {
      setIsInputValid(false)
    }
  }

  const slippageTipsLiteral = isInputValid
    ? t('slippage_normal_tips')
    : t('slippage_invalid_tips', {
        min: min.toFixed(1),
        max: max.toFixed(1),
      })

  return (
    <Box
      visible={isOpenModal}
      onBack={onClosed}
      title={t('settings')}
      bodyClassName="settingsModal"
    >
      <PayGasFeeSetting />
      <StyledSlippageBox>
        <RowBetween>
          <StyledLabelWrapper>
            <StyledIcon src={IconSlippage} />
            <StyledLabel>{t('set_slippage')}</StyledLabel>
          </StyledLabelWrapper>
          <span className="value">
            {`${slippageValue.toFixed(1)}%`}
            {isDefaultSlippage && `(${t('default')})`}
          </span>
        </RowBetween>
        <RowBetween margin="13.5px 0 0" gap="8px">
          <StyledSlippageSelector>
            {slippageSelectorValue.map((value, index) => (
              <StyledSlippageSelectorItem
                active={Number(value) === slippageValue && !isInputFocus}
                key={index}
                onClick={() => handleClickSlippageSelector(value)}
              >
                {value}%
              </StyledSlippageSelectorItem>
            ))}
          </StyledSlippageSelector>
          <StyledSlippageInput
            inputMode="decimal"
            placeholder={t('slippage_placeholder', {
              min: min.toFixed(1),
              max: max.toFixed(1),
            })}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onChange={handleInputSlippage}
            value={inputValue}
            active={isCustomSlippage}
            invalid={!isInputValid}
          />
        </RowBetween>
        <StyledTips invalid={!isInputValid}>{slippageTipsLiteral}</StyledTips>
      </StyledSlippageBox>
      <AuthorizationManage />
    </Box>
  )
}

export default SettingsModal
